(() => {
  const SELECTOR = '.bm-product-main-section';
  const DESCRIPTION_SELECTOR = '[data-bm-product-description]';
  const DESCRIPTION_SLOT_SELECTOR = '[data-bm-product-description-mobile-slot]';
  const MISMATCH_SELECTOR = '.bm-vehicle-compatibility.is-warning';
  const descriptionMedia = window.matchMedia('(max-width: 989px)');
  let descriptionState = null;

  const getRoots = (scope = document) => {
    const roots = [];
    if (scope.matches?.(SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(SELECTOR));
    return roots;
  };

  const initFitmentConfirmation = (scope = document) => {
    getRoots(scope).forEach((root) => {
      if (root.dataset.bmFitmentGuardInitialized === 'true') return;

      const productForm = root.querySelector('product-form');
      const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
      const submitButton = form?.querySelector('.button-add-to-cart, button[type="submit"]');
      if (!productForm || !form || !submitButton) return;

      root.dataset.bmFitmentGuardInitialized = 'true';
      const controller = new AbortController();
      const { signal } = controller;
      let allowNextSubmit = false;
      let confirmation = null;

      const isMismatch = () => Boolean(root.querySelector(MISMATCH_SELECTOR));

      const removeConfirmation = () => {
        confirmation?.remove();
        confirmation = null;
        allowNextSubmit = false;
      };

      const getConfirmation = () => {
        if (confirmation?.isConnected) return confirmation;

        confirmation = document.createElement('div');
        confirmation.className = 'bm-fitment-confirmation';
        confirmation.hidden = true;
        confirmation.setAttribute('role', 'alert');
        confirmation.innerHTML = [
          '<strong>Controleer de passing</strong>',
          '<p>Deze set is niet gekoppeld aan de auto die je hebt geselecteerd. Wil je deze set toch toevoegen?</p>',
          '<button type="button" class="bm-fitment-confirmation__button" data-bm-fitment-confirm>Toch toevoegen</button>'
        ].join('');

        const buttons = form.querySelector('.product-form__buttons');
        if (buttons?.parentNode) {
          buttons.insertAdjacentElement('afterend', confirmation);
        } else {
          form.appendChild(confirmation);
        }

        confirmation.querySelector('[data-bm-fitment-confirm]')?.addEventListener('click', () => {
          if (!isMismatch()) {
            removeConfirmation();
            form.requestSubmit(submitButton);
            return;
          }
          allowNextSubmit = true;
          confirmation.hidden = true;
          form.requestSubmit(submitButton);
        }, { signal });

        return confirmation;
      };

      const showConfirmation = () => {
        const panel = getConfirmation();
        panel.hidden = false;
        panel.querySelector('[data-bm-fitment-confirm]')?.focus({ preventScroll: true });
        panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      };

      form.addEventListener('submit', (event) => {
        if (!isMismatch()) {
          removeConfirmation();
          return;
        }
        if (allowNextSubmit) {
          allowNextSubmit = false;
          return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        showConfirmation();
      }, { capture: true, signal });

      const compatibilityObserver = new MutationObserver(() => {
        if (!isMismatch()) removeConfirmation();
      });
      compatibilityObserver.observe(root, { subtree: true, attributes: true, attributeFilter: ['class'] });

      ['bm:vehicle:ready', 'bm:vehicle:change'].forEach((eventName) => {
        document.addEventListener(eventName, () => {
          window.requestAnimationFrame(() => {
            if (!isMismatch()) removeConfirmation();
          });
        }, { signal });
      });

      root._bmFitmentGuardDestroy = () => {
        controller.abort();
        compatibilityObserver.disconnect();
        removeConfirmation();
        delete root.dataset.bmFitmentGuardInitialized;
      };
    });
  };

  const initStickyBuy = (scope = document) => {
    getRoots(scope).forEach((root) => {
      const sticky = root.querySelector('[data-bm-sticky-buy]');
      if (!sticky || sticky.dataset.bmInitialized === 'true') return;

      const mainForm = root.querySelector('product-form');
      const mainButton = mainForm?.querySelector('.button-add-to-cart, button[type="submit"]');
      const stickyButton = sticky.querySelector('[data-bm-sticky-buy-button]');
      if (!mainForm || !mainButton || !stickyButton) return;

      sticky.dataset.bmInitialized = 'true';
      const controller = new AbortController();
      const { signal } = controller;
      let framePending = false;

      const overlayIsOpen = () => {
        if (document.documentElement.classList.contains('bm-mobile-shell-open')) return true;
        if (document.body.classList.contains('drawer-open')) return true;
        if (document.body.classList.contains('bm-vehicle-dialog-open')) return true;
        return Boolean(document.querySelector(
          '.bm-mobile-menu-overlay:not([hidden]), .bm-mobile-search-overlay:not([hidden]), '
          + '[data-bm-vehicle-dialog]:not([hidden]), dialog[open], product-modal[open], .product-media-modal[open]'
        ));
      };

      const setVisible = (show) => {
        const visible = Boolean(
          show
          && window.innerWidth < 750
          && !mainButton.disabled
          && !mainButton.hasAttribute('disabled')
          && !overlayIsOpen()
        );
        sticky.hidden = !visible;
        sticky.classList.toggle('is-visible', visible);
        document.body.classList.toggle('bm-sticky-buy-active', visible);
      };

      const updatePosition = () => {
        framePending = false;
        const rect = mainButton.getBoundingClientRect();
        setVisible(rect.bottom <= 0);
      };

      const requestUpdate = () => {
        if (framePending) return;
        framePending = true;
        window.requestAnimationFrame(updatePosition);
      };

      stickyButton.addEventListener('click', () => {
        if (!mainButton.disabled && !mainButton.hasAttribute('disabled')) mainButton.click();
      }, { signal });

      ['scroll', 'resize', 'pageshow'].forEach((eventName) => {
        window.addEventListener(eventName, requestUpdate, { passive: true, signal });
      });

      [
        'cart-drawer:open', 'cart-drawer:close',
        'mobile-menu:open', 'mobile-menu:close',
        'product:modal-open', 'product:modal-close'
      ].forEach((eventName) => document.addEventListener(eventName, requestUpdate, { signal }));

      const buttonObserver = new MutationObserver(requestUpdate);
      buttonObserver.observe(mainButton, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-disabled'] });

      root._bmStickyBuyDestroy = () => {
        controller.abort();
        buttonObserver.disconnect();
        document.body.classList.remove('bm-sticky-buy-active');
      };

      requestUpdate();
    });
  };

  const restoreProductDescription = () => {
    if (!descriptionState) return;
    const { description, placeholder } = descriptionState;
    if (placeholder.parentNode) placeholder.parentNode.insertBefore(description, placeholder.nextSibling);
    description.classList.remove('bm-product-main__description--mobile');
  };

  const syncProductDescriptionOrder = () => {
    const description = document.querySelector(DESCRIPTION_SELECTOR) || descriptionState?.description;
    if (!description) {
      descriptionState = null;
      return;
    }
    const slot = document.querySelector(DESCRIPTION_SLOT_SELECTOR);
    if (!slot) {
      restoreProductDescription();
      return;
    }
    if (!descriptionState || descriptionState.description !== description || !descriptionState.placeholder.parentNode) {
      const mainRoot = description.closest(SELECTOR);
      if (!mainRoot || !description.parentNode) return;
      const placeholder = document.createComment('bm-product-description-home');
      description.parentNode.insertBefore(placeholder, description);
      descriptionState = { description, placeholder };
    }
    if (descriptionMedia.matches) {
      if (description.parentNode !== slot) slot.appendChild(description);
      description.classList.add('bm-product-main__description--mobile');
    } else {
      restoreProductDescription();
    }
  };

  const initPage = (scope = document) => {
    initFitmentConfirmation(scope);
    initStickyBuy(scope);
    syncProductDescriptionOrder();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPage(), { once: true });
  } else {
    initPage();
  }

  if (typeof descriptionMedia.addEventListener === 'function') {
    descriptionMedia.addEventListener('change', syncProductDescriptionOrder);
  } else if (typeof descriptionMedia.addListener === 'function') {
    descriptionMedia.addListener(syncProductDescriptionOrder);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initFitmentConfirmation(event.target);
    initStickyBuy(event.target);
    window.requestAnimationFrame(syncProductDescriptionOrder);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (descriptionState?.description && event.target.contains(descriptionState.description)) restoreProductDescription();
    const root = event.target.matches?.(SELECTOR) ? event.target : event.target.querySelector?.(SELECTOR);
    if (root) {
      root._bmFitmentGuardDestroy?.();
      root._bmStickyBuyDestroy?.();
      if (descriptionState?.placeholder && event.target.contains(descriptionState.placeholder)) {
        restoreProductDescription();
        descriptionState = null;
      }
    }
    window.requestAnimationFrame(syncProductDescriptionOrder);
  });
})();
